<template>
    <div class="live-feed">
        <div v-if="loading && !items.length" class="p-4 text-center">
            <div class="spinner-border spinner-border-sm"></div>
        </div>

        <transition-group name="feed" tag="div">
            <div
                v-for="item in displayItems"
                :key="item.id || item.signature"
                :class="['feed-item', { 'feed-item--new': item.isNew }]"
            >
                <div :class="['feed-icon', `feed-icon--${item.type || 'default'}`]">
                    <span>{{ getIcon(item.type) }}</span>
                </div>

                <div class="feed-content">
                    <div class="feed-title">{{ item.title }}</div>
                    <div class="feed-meta">
                        <span v-if="item.program">{{ item.program }}</span>
                        <span v-if="item.signature">
                            <a :href="`https://solscan.io/tx/${item.signature}`" target="_blank">
                                {{ item.signature.substring(0, 8) }}...
                            </a>
                        </span>
                    </div>
                </div>

                <div v-if="item.value" class="feed-value">
                    <div class="value" :class="item.valueClass">
                        {{ formatValue(item.value, item.format) }}
                    </div>
                    <div v-if="item.unit" class="unit">{{ item.unit }}</div>
                </div>

                <div class="feed-time">{{ formatTime(item.timestamp) }}</div>
            </div>
        </transition-group>

        <div v-if="!loading && !items.length" class="p-4 text-center text-muted">
            No activity yet
        </div>
    </div>
</template>

<script>
export default {
    name: 'LiveFeed',
    props: {
        items: {
            type: Array,
            default: () => []
        },
        maxItems: {
            type: Number,
            default: 50
        },
        wsUrl: {
            type: String,
            default: null
        },
        apiUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            localItems: [],
            loading: false,
            ws: null
        };
    },
    computed: {
        displayItems() {
            const items = this.localItems.length ? this.localItems : this.items;
            return items.slice(0, this.maxItems);
        }
    },
    mounted() {
        if (this.wsUrl) {
            this.connectWebSocket();
        } else if (this.apiUrl) {
            this.fetchInitialData();
        } else {
            this.localItems = [...this.items];
        }
    },
    beforeUnmount() {
        if (this.ws) {
            this.ws.close();
        }
    },
    methods: {
        getIcon(type) {
            const icons = {
                swap: '↔️',
                transfer: '→',
                mint: '✨',
                burn: '🔥',
                stake: '📌',
                vote: '🗳️',
                default: '•'
            };
            return icons[type] || icons.default;
        },

        formatValue(value, format) {
            if (!value) return '';
            switch (format) {
                case 'sol':
                    return (value / 1e9).toFixed(4);
                case 'lamports':
                    return Number(value).toLocaleString();
                case 'usd':
                    return '$' + Number(value).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    });
                case 'compact':
                    return Number(value).toLocaleString(undefined, {
                        notation: 'compact',
                        maximumFractionDigits: 1
                    });
                default:
                    return value;
            }
        },

        formatTime(timestamp) {
            if (!timestamp) return '';
            const date = new Date(timestamp);
            const now = new Date();
            const diff = (now - date) / 1000;

            if (diff < 60) return `${Math.floor(diff)}s ago`;
            if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
            if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
            return date.toLocaleDateString();
        },

        addItem(item) {
            // Add to front with animation flag
            this.localItems.unshift({
                ...item,
                isNew: true
            });

            // Remove animation flag after delay
            setTimeout(() => {
                const idx = this.localItems.findIndex(i =>
                    i.id === item.id || i.signature === item.signature
                );
                if (idx >= 0) {
                    this.localItems[idx].isNew = false;
                }
            }, 500);

            // Trim excess items
            if (this.localItems.length > this.maxItems) {
                this.localItems = this.localItems.slice(0, this.maxItems);
            }
        },

        async fetchInitialData() {
            if (!this.apiUrl) return;
            this.loading = true;
            try {
                const response = await axios.get(this.apiUrl);
                this.localItems = response.data || [];
            } catch (error) {
                console.error('LiveFeed fetch error:', error);
            } finally {
                this.loading = false;
            }
        },

        connectWebSocket() {
            if (!this.wsUrl) return;

            this.ws = new WebSocket(this.wsUrl);

            this.ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (Array.isArray(data)) {
                    data.forEach(item => this.addItem(item));
                } else {
                    this.addItem(data);
                }
            };

            this.ws.onclose = () => {
                setTimeout(() => this.connectWebSocket(), 5000);
            };
        }
    },
    watch: {
        items: {
            handler(newItems) {
                if (!this.wsUrl && !this.apiUrl) {
                    this.localItems = [...newItems];
                }
            },
            deep: true
        }
    }
};
</script>

<style scoped>
.feed-enter-active {
    transition: all 0.3s ease;
}

.feed-leave-active {
    transition: all 0.2s ease;
}

.feed-enter-from {
    opacity: 0;
    transform: translateY(-20px);
}

.feed-leave-to {
    opacity: 0;
}
</style>
